/**
 * MobileNavOverlay — the app-switcher launcher, opened by tapping the logo in
 * MobileHeader. Modeled on a phone OS app switcher: every top-level
 * destination (Drive / Workspace / Keybook) is a large icon tile in a
 * CENTER-MODE carousel — the centered tile scales up ("hovered"), and
 * dragging/scrolling the strip snaps the next tile into center. Mini dots +
 * a thin scrollbar under the strip indicate position/count. Tap a tile to
 * navigate; tap the fog (or Esc) to dismiss — no close button.
 *
 * The fog follows DESIGN_DOC.md §10.5 (transient-popover glass, the rule-2
 * carve-out): translucent tint + `blur + saturate` backdrop-filter, tuned per
 * theme in index.css (.mobile-fog). Scroll hot path writes transforms
 * directly to the DOM (rAF-coalesced), React state only for the focused-dot
 * change — DESIGN_DOC rule 6.
 *
 * Portals to document.body (outside any ThemedArea), so it applies the theme
 * class explicitly — same rule the AvatarMenu / connection popups follow.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ComponentType, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Sun, Eclipse, Moon, Globe, Pin, PinOff, ArrowLeft, FolderOutput, FolderInput, X } from 'lucide-react'
import { useTheme, getThemeClass, avatarBg, getInitials, OrgCreateDialog, useShell, MiniappIcon, composeRailEntries, FolderGlyph, type RailApp } from '@papan/core-ui/ui'
import type { MiniappIconSpec } from '@papan/types'

// Theme segments for the launcher's Preview/Source-style pill (.mode-pill),
// light → dim → dark. ICON-ONLY, equal fixed width, so the sliding chip's
// position is computed inline (index × SEG) and animates on a plain re-render.
const THEME_OPTS = [
  { value: 'light' as const, icon: <Sun size={15} />, title: 'Light theme' },
  { value: 'dim' as const, icon: <Eclipse size={15} />, title: 'Dim theme' },
  { value: 'dark' as const, icon: <Moon size={15} />, title: 'Dark theme' },
]
/** Per-segment size (px) — square so the chip reads as a full circle; the
 *  .mode-pill padding is 4px (see index.css), so the chip height = SEG too. */
const THEME_SEG = 34
/** The launcher accent = the DIM theme's --btn-primary-bg (indigo). Used for
 *  the theme chip + active dot in EVERY theme, so light/dark don't turn blue. */
const LAUNCHER_ACCENT = '#4f46e5'
/** Cover-flow scale. The centered glyph reaches its FULL rendered size (scale 1)
 *  and neighbors shrink toward GLYPH_REST_SCALE. Glyphs are rendered at that
 *  full (centered) size and only ever scaled DOWN — so the composited layer's
 *  texture is never upsampled, which is what rasterized (blurred) the SVG icon
 *  when we scaled a small render UP. Rest render sizes therefore = final ×1.6. */
const GLYPH_REST_SCALE = 0.625 // 1 / 1.6
import { orgsApi } from '@papan/orgs/client'
import { workspaceApi } from '@papan/workspace/client'
import { useNewProject } from '@papan/files/ui'
import { useAuth } from '../hooks/useAuth'
import { useTenant } from '../hooks/useTenant'
import { pinShellApp, unpinShellApp, closeShellInstance, setShellPinFolder } from '../hooks/useShellPersistence'
import { useMobileTabs, activeTab } from './nav'
import { MobileSheet } from './MobileSheet'
import { useIdentityOnly } from './useIdentityOnly'
import { useLandingPath } from '../lib/landing'
import { useOrgCreateLimit } from '../hooks/useOrgCreateLimit'
import { personalTenantIsIdentityOnly } from '../lib/identityShell'
import {
  AccountSheet, AccountActionCard, LogOutCard, type IdentityRow,
} from '@papan/core-ui/ui'

export function MobileNavOverlay({ prefix, onClose }: { prefix: string; onClose: () => void }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { settings, updateSetting } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  // The launcher themes off the workspace area; syncAll flips every area at
  // once (same rule as ThemeToggle / the Settings page).
  const setThemeMode = (next: 'light' | 'dim' | 'dark') => {
    if (settings.theme.syncAll) updateSetting('theme', { ...settings.theme, drive: next, workspace: next, chat: next })
    else updateSetting('theme', { ...settings.theme, workspace: next })
  }
  // The pill shows a LOCAL value that flips immediately on tap, so its chip
  // slides on a plain value change; the real theme is applied only AFTER the
  // slide settles (~0.22s). Re-theming the launcher re-renders its whole
  // subtree and defeats the chip's CSS transition (it snaps to the segment),
  // so we let the slide finish first, THEN recolor.
  const [pillTheme, setPillTheme] = useState<'light' | 'dim' | 'dark'>(settings.theme.workspace)
  useEffect(() => { setPillTheme(settings.theme.workspace) }, [settings.theme.workspace])
  const themeApplyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(themeApplyTimer.current), [])
  const pickTheme = (next: 'light' | 'dim' | 'dark') => {
    setPillTheme(next)
    clearTimeout(themeApplyTimer.current)
    themeApplyTimer.current = setTimeout(() => setThemeMode(next), 240)
  }
  // Slider chip position, set IMPERATIVELY after paint. Setting `left` during
  // render (JSX / layout effect) skips the CSS transition — the browser never
  // paints the old value in a fresh frame — but a post-paint style write (like
  // a plain JS assignment on a settled element) animates. Mount sets it with
  // no animation (useLayoutEffect, before first paint).
  const themeSliderRef = useRef<HTMLSpanElement>(null)
  const themeIdx = Math.max(0, THEME_OPTS.findIndex((o) => o.value === pillTheme))
  const themeSliderLeft = `${4 + themeIdx * THEME_SEG}px`
  useLayoutEffect(() => { if (themeSliderRef.current) themeSliderRef.current.style.left = themeSliderLeft }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (themeSliderRef.current) themeSliderRef.current.style.left = themeSliderLeft }, [themeSliderLeft])
  const active = activeTab(location.pathname)
  // activeTab() has no 'app' bucket — an /app/<ref> route falls back to 'drive',
  // which would light up the Drive tile while you're actually inside an app. So
  // detect the app route explicitly: on it, the matching APP tile is current
  // (and nothing else), NOT Drive. Same shape MobileHeader uses for its cutout.
  const currentAppRef = location.pathname.match(/\/app\/([^/?]+)/)?.[1]

  // Carousel source = the static top-level tabs PLUS a tile per running user
  // app (mini-app / standalone HTML), read from the SHARED shell store — the
  // same store desktop's dock reads (TaskBar userApps). Registration happens in
  // MobileAppScreen on open; here we just surface them, so switching apps works
  // exactly like the desktop dock. nav.tsx stays the static-tabs source of
  // truth — the merge lives here so the two can't drift.
  const { runningApps, pinnedApps, folders, isPinned, pinApp, unpinApp, isRunning, closeApp, setAppIcon, movePinToFolder } = useShell()
  // ONE derivation, two readers (the carousel and the account sheet below) —
  // not a second `useIdentityOnly()` call, per §9.2.
  const identityOnly = useIdentityOnly()
  const mobileTabs = useMobileTabs(identityOnly)
  // Where closing the app you stand in lands: HOME, not a hardcoded chat —
  // a launcher deployment has no chat home at all (#368).
  const homePath = useLandingPath(prefix, identityOnly ? 'identity-only' : 'full')
  const canCreateOrg = useOrgCreateLimit()
  type LauncherTile =
    | { type: 'tab'; key: string; label: string; icon: ComponentType<{ size?: number; className?: string }>; path: string }
    | { type: 'app'; key: string; label: string; kind: 'miniapp' | 'html'; ref: string; icon?: MiniappIconSpec | null; folderId: string | null }
    /** A taskbar app folder (0195): tapping it steps INTO it — the carousel
     *  then shows its members behind a back tile. */
    | { type: 'folder'; key: string; label: string; id: string; members: RailApp[]; active: boolean; running: boolean }
    | { type: 'back'; key: string; label: string }
  // The SAME composition the desktop rail renders (pins and folders in dock
  // order, then running-unpinned apps), so the two launchers cannot drift.
  const entries = composeRailEntries({ running: runningApps, pins: pinnedApps, folders, activeRef: currentAppRef ?? null })
  // Which folder the carousel is inside, if any. Derived against the live
  // entries so a folder pruned under us (its last member removed here, or on
  // another device) drops the carousel back to the top level by itself.
  const [openFolderId, setOpenFolderId] = useState<string | null>(null)
  const openFolder = openFolderId ? entries.find((e) => e.type === 'folder' && e.id === openFolderId) : undefined
  const appTile = (a: RailApp): LauncherTile => ({
    type: 'app', key: `app:${a.kind}:${a.ref}`, label: a.title || 'App', kind: a.kind, ref: a.ref, icon: a.icon, folderId: a.folderId,
  })
  const tiles: LauncherTile[] = openFolder && openFolder.type === 'folder'
    ? [
        { type: 'back', key: 'back', label: openFolder.title || 'Folder' },
        ...openFolder.members.map(appTile),
      ]
    : [
        // useMobileTabs(), not the raw MOBILE_TABS const: the chat tab's label is
        // the brand product name and is only resolved by the hook.
        ...mobileTabs.map((t): LauncherTile => ({ type: 'tab', key: t.key, label: t.label, icon: t.icon, path: t.path })),
        ...entries.map((e): LauncherTile => e.type === 'folder'
          ? { type: 'folder', key: `folder:${e.id}`, label: e.title || 'Folder', id: e.id, members: e.members, active: e.active, running: e.running }
          : appTile(e)),
      ]
  // "Remove from folder" (the control under a member tile while inside a
  // folder) — local mirror first, then the server write, like the pin toggle.
  const removeFromFolder = (tile: Extract<LauncherTile, { type: 'app' }>) => {
    movePinToFolder(tile.kind, tile.ref, null)
    void setShellPinFolder(tile.kind, tile.ref, null).catch(() => {})
  }
  // Pin toggle (the small pin under an app tile) — local shell state first,
  // then the server write, exactly like desktop's handlePin/UnpinUserApp.
  const togglePin = (tile: Extract<LauncherTile, { type: 'app' }>) => {
    if (isPinned(tile.kind, tile.ref)) {
      unpinApp(tile.kind, tile.ref)
      void unpinShellApp(tile.kind, tile.ref).catch(() => {})
    } else {
      pinApp(tile.kind, tile.ref, tile.label)
      // Copy the live icon spec onto the pin entry — pinApp doesn't, so the
      // tile would flip to the name-derived default the moment the running
      // entry goes away (swipe-close). setAppIcon patches pins in place.
      if (tile.icon != null) setAppIcon(tile.kind, tile.ref, tile.icon)
      void pinShellApp(tile.kind, tile.ref, tile.label).catch(() => {})
    }
  }
  // The current tile: on an /app/<ref> route it's the matching app tile; else
  // the active top-level tab. Drives the is-current accent AND which tile the
  // carousel opens centered on.
  const isCurrentTile = (t: LauncherTile) =>
    currentAppRef
      ? (t.type === 'app' && t.ref === currentAppRef) || (t.type === 'folder' && t.active)
      : t.type === 'tab' && t.key === active
  const currentIndex = Math.max(0, tiles.findIndex(isCurrentTile))

  const { user, signOut } = useAuth()
  const { tenantId, activeOrgEmail, switchTenant } = useTenant()
  const queryClient = useQueryClient()
  const newProject = useNewProject()
  // Second frosted layer: the account sheet (opened by the bottom pill).
  const [accountsOpen, setAccountsOpen] = useState(false)
  const [showOrgCreate, setShowOrgCreate] = useState(false)
  const accountsOpenRef = useRef(accountsOpen)
  accountsOpenRef.current = accountsOpen

  // Same source/cache key as the header's AvatarMenu, so the launcher costs
  // no extra fetch.
  const tenantsQuery = useQuery({
    queryKey: ['mobile-my-tenants'],
    queryFn: () => orgsApi.listMyTenants(),
    staleTime: 60_000,
  })
  const tenants = tenantsQuery.data?.tenants ?? []
  const personalIsIdentityOnly = personalTenantIsIdentityOnly(tenants)

  // Flatten tenants into switchable IDENTITIES: one row per tenant, except
  // multi-email orgs get one row per email (the "tenant · email (role)"
  // identity shape used by the consent pickers) — a vertical, scrollable
  // list scales where an avatar strip wouldn't.
  //
  // §8.1: the personal row is dropped when THE PERSONAL TENANT is identity-only
  // — not when the ACTIVE one is. Keying it off the active tenant meant the row
  // came back the moment this person entered an org, offering a one-click route
  // to a drive that does not exist. `personalTenantIsIdentityOnly` is the shared
  // predicate, so this cannot drift from the desktop menu.
  const identityRows: IdentityRow[] = tenants.flatMap((t): IdentityRow[] => {
    if (t.type === 'personal') {
      if (personalIsIdentityOnly) return []
      return [{
        key: t.id,
        tenantId: t.id,
        kind: 'personal',
        label: user?.name ?? 'Personal',
        sub: user?.email,
        tag: 'Personal',
        seed: user?.email ?? t.name,
        initials: getInitials(user?.name ?? 'Personal'),
        active: t.id === tenantId,
      }]
    }
    if (t.emails.length > 1) {
      return t.emails.map((e) => ({
        key: `${t.id}:${e.email}`,
        tenantId: t.id,
        kind: 'organization',
        email: e.email,
        label: t.name,
        sub: e.email,
        tag: e.role,
        seed: t.name,
        initials: getInitials(t.name),
        active: t.id === tenantId && (activeOrgEmail ?? user?.email) === e.email,
      }))
    }
    return [{
      key: t.id,
      tenantId: t.id,
      kind: 'organization',
      email: t.emails[0]?.email,
      label: t.name,
      sub: t.emails[0]?.email,
      tag: t.emails[0]?.role,
      seed: t.name,
      initials: getInitials(t.name),
      active: t.id === tenantId,
    }]
  })
  const activeIdentity = identityRows.find((r) => r.active)
  // The account pill falls back to the signed-in account when no identity row is
  // active, so it is NEVER absent. `activeIdentity` is undefined whenever the
  // URL scopes to a tenant the account list doesn't contain — a mis-scoped deep
  // link, or a tenant-scoped `my-tenants` that itself 401'd (org_email_required)
  // and emptied `identityRows`. Hiding the pill there stranded the user with no
  // in-app route to switch identity, create an org, or sign out; the recovery
  // dialog in MobileShell handles the org_email_required case, and this makes
  // the launcher a dead end in no case. The sheet's own rows stay accurate — a
  // fallback here changes only whether the entry point is reachable.
  const pillDisplay = activeIdentity ?? (user
    ? {
        label: user.name ?? user.email ?? 'Account',
        seed: user.email ?? 'account',
        initials: getInitials(user.name ?? user.email ?? 'Account'),
      }
    : null)

  const pickIdentity = (row: IdentityRow) => {
    if (row.active) { setAccountsOpen(false); return }
    switchTenant(row.tenantId, row.email)
    // Stay on the launcher after switching — just close the account sheet
    // back to the carousel (don't tear the whole launcher down with onClose).
    setAccountsOpen(false)
  }

  // Keep the pill while the account list leaves a clear gap below it; hide it
  // only when the list grows tall enough to crowd it. Measured (rule 5) as the
  // real gap between the LAST card's bottom edge and the viewport bottom — the
  // band the pill occupies (bottom-10 + its own height ≈ 84px). offsetTop/
  // offsetHeight are transform-independent, so the reading is stable across
  // the slide-in (getBoundingClientRect wasn't — it flickered mid-animation,
  // the old "pill sometimes shows, sometimes not"). No halving: offsetTop
  // already accounts for wherever the centered list sits, so the difference IS
  // the bottom gap, not the total empty space.
  const sheetRef = useRef<HTMLDivElement>(null)
  const [pillFits, setPillFits] = useState(true)
  useLayoutEffect(() => {
    if (!accountsOpen) { setPillFits(true); return }
    const measure = () => {
      const el = sheetRef.current
      if (!el) return
      const last = el.lastElementChild as HTMLElement | null
      if (!last) return
      const gapBelow = window.innerHeight - (last.offsetTop + last.offsetHeight)
      setPillFits(gapBelow >= 84)
    }
    measure() // synchronous — offset metrics are settled in useLayoutEffect
    // Re-measure after the slide-in settles, plus on resize / content change.
    requestAnimationFrame(() => requestAnimationFrame(measure))
    const ro = new ResizeObserver(measure)
    if (sheetRef.current) ro.observe(sheetRef.current)
    window.addEventListener('resize', measure)
    return () => { ro.disconnect(); window.removeEventListener('resize', measure) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsOpen, identityRows.length])

  const stripRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  // NATIVE scroll: the strip is a real overflow-x scroller with CSS scroll-snap
  // + native iOS momentum/rubber-band — it runs on the COMPOSITOR thread (buttery
  // 120Hz, decoupled from JS). We only listen to the scroll event (rAF-coalesced)
  // to drive the cover-flow tile scale; the scroll itself is never touched by JS.
  const raf = useRef(0)
  const [focused, setFocused] = useState(currentIndex)
  // True once a tile tap starts the "dive into the app" zoom — freezes further
  // taps and drives the root's mobile-launcher-out animation.
  const [launching, setLaunching] = useState(false)

  // Re-apply the slider position when the theme pill REMOUNTS. The pill is
  // conditionally rendered (`!launching && !accountsOpen`), so opening then
  // closing the account sheet unmounts + remounts it with a fresh <span> that
  // has no inline `left` — and the mount-only + theme-change effects above don't
  // fire, so the slider snapped to the pill's left edge. Pre-paint
  // (useLayoutEffect) so it appears already in place (no slide), and keyed ONLY
  // on the visibility flags — NOT themeSliderLeft — so a genuine theme change
  // keeps the animated slide from the effect above instead of jumping here.
  useLayoutEffect(() => {
    if (!accountsOpen && !launching && themeSliderRef.current) {
      themeSliderRef.current.style.left = themeSliderLeft
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsOpen, launching])

  useEffect(() => {
    // Esc peels one layer: account sheet first, then the launcher.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (accountsOpenRef.current) setAccountsOpen(false)
      else onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Geometry cache — tile centers, viewport width, max scroll. Measured ONCE
  // per open + on resize so the scroll hot path never reads offsetLeft /
  // clientWidth. Those per-frame layout reads (interleaved with the transform
  // writes below) forced a reflow every frame — the dropped launcher frames.
  const geomRef = useRef<{ centers: number[]; clientW: number; max: number }>({ centers: [], clientW: 0, max: 0 })
  const measureGeom = () => {
    const strip = stripRef.current
    if (!strip) return
    const clientW = strip.clientWidth
    const centers = Array.from(strip.children).map((c) => {
      const el = c as HTMLElement
      return el.offsetLeft + el.offsetWidth / 2
    })
    geomRef.current = { centers, clientW, max: Math.max(0, strip.scrollWidth - clientW) }
  }

  /** Cover-flow scale by distance-to-center, CONTINUOUS (1.0 → 1.18 at dead-
   *  center). The influence reaches 1.5× the tile PITCH, so the centered tile
   *  AND its closest neighbor grow, while anything a further pitch out is past
   *  the falloff and stays flat at rest size. Applied to the inner glyph only —
   *  the container/selection well never scales, and the label (a sibling of the
   *  container) keeps a constant size/position. Uses CACHED centers, so the hot
   *  path does ONE scrollLeft read and zero offsetLeft reads (which reflow). */
  const applyScrollEffects = () => {
    const strip = stripRef.current
    if (!strip) return
    const { centers, clientW } = geomRef.current
    const mid = strip.scrollLeft + clientW / 2
    const pitch = centers.length > 1 ? Math.abs(centers[1] - centers[0]) : clientW
    const influence = pitch * 1.5
    let best = 0, bestDist = Infinity
    const kids = strip.children
    for (let i = 0; i < centers.length; i++) {
      const dist = Math.abs(centers[i] - mid)
      if (dist < bestDist) { bestDist = dist; best = i }
      const glyph = (kids[i] as HTMLElement).querySelector('.mobile-switcher-glyph') as HTMLElement | null
      if (!glyph) continue
      const t = Math.max(0, 1 - dist / influence)
      // Scale DOWN from full size (rest) to 1.0 (centered) — never above 1, so
      // the crisp max-size render is only ever minified, not upscaled/blurred.
      glyph.style.transform = `scale(${GLYPH_REST_SCALE + (1 - GLYPH_REST_SCALE) * t})`
      glyph.style.opacity = String(0.88 + 0.12 * t)
    }
    setFocused((f) => (f === best ? f : best))
  }

  // NATIVE scroll fires this (compositor-driven). Coalesce the main-thread scale
  // update to one rAF per frame so the scroll itself stays glassy.
  const onScroll = () => {
    cancelAnimationFrame(raf.current)
    raf.current = requestAnimationFrame(applyScrollEffects)
  }

  /** scrollLeft that centers tile i (cached geometry). */
  const tileTarget = (i: number) => {
    const { centers, clientW } = geomRef.current
    return (centers[i] ?? 0) - clientW / 2
  }

  /** Center a tile via NATIVE scroll (compositor-smooth). smooth=true animates. */
  const centerTile = (i: number, smooth: boolean) => {
    const strip = stripRef.current
    if (!strip) return
    strip.scrollTo({ left: tileTarget(i), behavior: smooth ? 'smooth' : 'auto' })
  }

  /** Tile whose center is nearest the current scroll-mid (cached geometry). */
  const nearestIndex = () => {
    const strip = stripRef.current
    if (!strip) return focused
    const { centers, clientW } = geomRef.current
    const mid = strip.scrollLeft + clientW / 2
    let best = 0, bestDist = Infinity
    for (let i = 0; i < centers.length; i++) {
      const d = Math.abs(centers[i] - mid)
      if (d < bestDist) { bestDist = d; best = i }
    }
    return best
  }

  // ── Mouse / pen drag-to-scroll ───────────────────────────────────────
  // A native overflow scroller only responds to TOUCH + trackpad, so on a
  // desktop a mouse click-drag can't scroll it. Fingers keep using native
  // momentum (we bail on pointerType 'touch'); mouse/pen drive scrollLeft
  // directly here, then snap to the nearest tile on release.
  const drag = useRef<{ x: number; scroll: number; moved: boolean; id: number } | null>(null)
  const suppressClick = useRef(false)
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'touch') return // native scroll owns fingers
    if (accountsOpen || showOrgCreate) return
    const strip = stripRef.current
    if (!strip) return
    suppressClick.current = false
    drag.current = { x: e.clientX, scroll: strip.scrollLeft, moved: false, id: e.pointerId }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current
    const strip = stripRef.current
    if (!d || !strip) return
    const dx = e.clientX - d.x
    if (!d.moved) {
      if (Math.abs(dx) <= 4) return // still a click, not a drag
      d.moved = true
      strip.classList.add('dragging')
      try { strip.setPointerCapture(d.id) } catch { /* not capturable */ }
    }
    strip.scrollLeft = d.scroll - dx // fires onScroll → applyScrollEffects
  }
  const endPointer = () => {
    const d = drag.current
    const strip = stripRef.current
    if (!d || !strip) return
    drag.current = null
    strip.classList.remove('dragging')
    try { strip.releasePointerCapture(d.id) } catch { /* already released */ }
    if (d.moved) {
      // Swallow the click a drag-release synthesizes (must not close/open),
      // then settle on the nearest tile — CSS snap won't fire after a manual
      // scrollLeft set, so drive it ourselves.
      suppressClick.current = true
      centerTile(nearestIndex(), true)
    }
  }
  // Plain mouse wheel is vertical; map it to horizontal so a wheel scrolls the
  // carousel. Trackpads that already emit deltaX keep their native h-scroll.
  const onWheel = (e: React.WheelEvent) => {
    const strip = stripRef.current
    if (!strip) return
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) strip.scrollLeft += e.deltaY
  }

  // ── Swipe-up-to-close (running user apps) ────────────────────────────
  // The OS-switcher close gesture: flick an app tile upward and it flies off +
  // closes. The strip's touch-action is pan-x, so the browser never consumes
  // VERTICAL touch moves — we track them per tile. Ownership rules: horizontal
  // intent wins via native scroll (touch: the browser fires pointercancel once
  // it claims the pan) or the strip's mouse-drag (drag.current.moved); an
  // upward-dominant move claims the gesture, captures the pointer, and
  // stopPropagation keeps the strip's mouse-drag from double-tracking it.
  const swipe = useRef<{ x: number; y: number; id: number; active: boolean; el: HTMLButtonElement } | null>(null)
  const isClosable = (t: LauncherTile): t is Extract<LauncherTile, { type: 'app' }> =>
    t.type === 'app' && isRunning(t.kind, t.ref)

  const closeTileApp = (tile: Extract<LauncherTile, { type: 'app' }>, el: HTMLButtonElement) => {
    // If this app is pinned, stamp its live icon onto the pin BEFORE the
    // running entry (the icon's only source) disappears — otherwise the
    // surviving pinned-idle tile flips to the name-derived default icon.
    if (tile.icon != null && isPinned(tile.kind, tile.ref)) setAppIcon(tile.kind, tile.ref, tile.icon)
    closeApp(tile.kind, tile.ref)
    void closeShellInstance(tile.kind, tile.ref).catch(() => {})
    // Closing the app you're standing IN: the page behind the launcher is now
    // dead — land on this deployment's home (#368: never a hardcoded chat).
    if (currentAppRef === tile.ref) navigate(homePath)
    // A pinned app keeps its tile (now idle, SAME key → same element), which
    // would otherwise stay stuck with the fly-off transform: clear it and fade
    // the idle tile back in. For an unpinned app the element unmounts and the
    // resets are harmless.
    el.style.transition = 'none'
    el.style.transform = ''
    requestAnimationFrame(() => {
      el.style.transition = 'opacity 0.25s ease'
      el.style.opacity = ''
      setTimeout(() => { el.style.transition = '' }, 260)
    })
  }

  // ── Long-press → the app's action sheet ──────────────────────────────
  // The launcher has no right-click and no drag, so pin / unpin, moving into
  // or out of a folder, and closing live behind a 500ms hold on an app tile.
  // Movement past 10px (a swipe, a scroll) cancels the hold; a fired hold
  // swallows the release's tap so it does not also launch the app.
  const [actionTile, setActionTile] = useState<Extract<LauncherTile, { type: 'app' }> | null>(null)
  const hold = useRef<{ timer: ReturnType<typeof setTimeout>; x: number; y: number } | null>(null)
  const cancelHold = () => { if (hold.current) { clearTimeout(hold.current.timer); hold.current = null } }
  const tilePointerDown = (e: React.PointerEvent<HTMLButtonElement>, tile: LauncherTile) => {
    if (launching || accountsOpen || showOrgCreate) return
    if (tile.type === 'app') {
      cancelHold()
      const timer = setTimeout(() => { hold.current = null; suppressClick.current = true; setActionTile(tile); setTimeout(() => { suppressClick.current = false }, 300) }, 500)
      hold.current = { timer, x: e.clientX, y: e.clientY }
    }
    if (!isClosable(tile)) return
    swipe.current = { x: e.clientX, y: e.clientY, id: e.pointerId, active: false, el: e.currentTarget }
  }
  const tilePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (hold.current && Math.hypot(e.clientX - hold.current.x, e.clientY - hold.current.y) > 10) cancelHold()
    const s = swipe.current
    if (!s) return
    const dx = e.clientX - s.x
    const dy = e.clientY - s.y
    if (!s.active) {
      if (drag.current?.moved) { swipe.current = null; return } // mouse h-drag owns this pointer
      if (dy > -10 || Math.abs(dy) <= Math.abs(dx)) return // not upward-dominant (yet)
      s.active = true
      suppressClick.current = true // the release must not read as a tap
      try { s.el.setPointerCapture(s.id) } catch { /* not capturable */ }
      s.el.style.transition = 'none'
    }
    e.stopPropagation() // captured events still bubble — keep the strip drag inert
    const off = Math.min(0, dy) // upward only; downward pull does nothing
    s.el.style.transform = `translateY(${off}px)`
    s.el.style.opacity = String(Math.max(0.2, 1 + off / 300))
  }
  const tilePointerEnd = (e: React.PointerEvent<HTMLButtonElement>, tile: LauncherTile, cancelled: boolean) => {
    cancelHold()
    const s = swipe.current
    if (!s) return
    swipe.current = null
    if (!s.active) return
    e.stopPropagation()
    try { s.el.releasePointerCapture(s.id) } catch { /* already released */ }
    // Let the release's synthesized click (guarded by pick) pass, then re-arm taps.
    setTimeout(() => { suppressClick.current = false }, 80)
    const el = s.el
    if (!cancelled && e.clientY - s.y < -70 && isClosable(tile)) {
      // Committed: fly off, then close (dragging back down below the threshold
      // before release aborts naturally).
      el.style.transition = 'transform 0.22s ease-in, opacity 0.22s ease-in'
      el.style.transform = 'translateY(-360px)'
      el.style.opacity = '0'
      setTimeout(() => closeTileApp(tile, el), 210)
    } else {
      el.style.transition = 'transform 0.2s ease, opacity 0.2s ease'
      el.style.transform = ''
      el.style.opacity = ''
      setTimeout(() => { el.style.transition = '' }, 220)
    }
  }

  // Open with the CURRENT page centered + effects applied before paint.
  useLayoutEffect(() => {
    measureGeom()
    centerTile(currentIndex, false)
    applyScrollEffects()
    return () => cancelAnimationFrame(raf.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // Re-measure on resize / orientation change so the cached geometry stays true.
  useEffect(() => {
    const onResize = () => { measureGeom(); applyScrollEffects() }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // Entering or leaving a folder swaps the tile list, so the cached centers
  // are for a strip that no longer exists: re-measure and start from the
  // first tile (the back tile, or the first top-level tab).
  //
  // NOT ON MOUNT. `openFolderId` starts null, so this also fired on the first
  // render — and being declared AFTER the effect above (React runs layout
  // effects in declaration order) it ran second and replaced "open centered on
  // the CURRENT page" with "scrolled to the first tile", so the launcher no
  // longer opened on the app you were in and the centered tile stopped
  // matching `aria-current`.
  //
  // Guard on the PREVIOUS VALUE, not a `mounted` boolean: StrictMode
  // double-invokes effects in dev (mount → cleanup → mount) and the second run
  // would see such a flag already set, making the guard a no-op exactly where
  // it is needed.
  const prevFolderId = useRef<string | null | undefined>(undefined)
  useLayoutEffect(() => {
    const first = prevFolderId.current === undefined
    if (prevFolderId.current === openFolderId) return
    prevFolderId.current = openFolderId
    if (first) return
    measureGeom()
    centerTile(0, false)
    applyScrollEffects()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFolderId])
  // Re-measure when the tile COUNT changes while open — pins hydrating after
  // mount (or a pin/unpin removing an idle tile) adds/removes strip children,
  // which would leave the cached centers stale.
  useEffect(() => {
    measureGeom()
    applyScrollEffects()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiles.length])

  // The Workspace tile enters a WORKSPACE, not the workspace list: the most
  // recently opened one (same "re-open where I was" behavior as desktop's
  // rail click), or a fresh Untitled when the tenant has none yet. The list
  // stays reachable via the workspace surface's back button.
  const recentWsQuery = useQuery({
    queryKey: ['mobile-recent-workspaces', tenantId, activeOrgEmail ?? ''],
    queryFn: () => workspaceApi.listRecent(),
    staleTime: 30_000,
  })
  const openLastWorkspace = async () => {
    try {
      // Fetch fresh (recency changes as the user moves around); fall back to
      // the shared cache if the request fails mid-flight.
      const recent = await workspaceApi.listRecent().catch(() => recentWsQuery.data ?? [])
      if (recent.length > 0) {
        navigate(`${prefix}/workspace/${recent[0].folderId}`)
        return
      }
      // No workspace yet → deliberate creation: the access dialog on org,
      // auto-create on personal (the provider decides via autoCreatePersonal).
      newProject.open({
        autoCreatePersonal: true,
        onDone: (folderId) => {
          void queryClient.invalidateQueries({ queryKey: ['mobile-workspaces'] })
          void queryClient.invalidateQueries({ queryKey: ['mobile-recent-workspaces'] })
          navigate(`${prefix}/workspace/${folderId}`)
        },
      })
    } catch {
      // e.g. org member without create permission — land on the list instead.
      navigate(`${prefix}/workspace`)
    }
  }

  // Navigate ONLY — the launcher is torn down separately (see launchTile) so
  // its zoom-out and the destination page's open animation can overlap.
  const navigateTile = (i: number) => {
    const tile = tiles[i]
    if (!tile) return
    // A running user-app tile → open its run route (leaves it running; this is
    // the "switch away" path, so it never calls closeApp).
    if (tile.type === 'app') { navigate(`${prefix}/app/${tile.ref}`); return }
    // Folder and back tiles are handled in `pick` and never launch; they are
    // excluded here so the tab branch below can rely on `path`.
    if (tile.type === 'folder' || tile.type === 'back') return
    if (tile.key === 'workspace') {
      // Navigate SYNCHRONOUSLY off the recent-workspaces CACHE so the workspace
      // route changes before the launcher unmounts. openLastWorkspace() awaits
      // a fresh listRecent() fetch (~0.5s), during which the launcher was
      // already gone — so the OLD page (drive/keybook) flashed through. Only
      // fall back to the async open (fetch / create-untitled) when the cache is
      // empty (fresh tenant with no workspaces).
      const recent = recentWsQuery.data
      if (recent && recent.length > 0) { navigate(`${prefix}/workspace/${recent[0].folderId}`); return }
      void openLastWorkspace()
      return
    }
    navigate(`${prefix}/${tile.path}`)
  }

  const pendingOpen = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => { clearTimeout(pendingOpen.current); clearTimeout(closeTimer.current); cancelAnimationFrame(raf.current) }, [])

  // Tapping a tile "opens the app": the launcher zooms up on its OPAQUE
  // backdrop (mobile-launcher-bg) while the icons fade out. The destination
  // page is NOT shown during the zoom — navigation fires only as the zoom
  // finishes (page mounts HIDDEN behind the opaque launcher), and the launcher
  // unmounts a hair later, revealing the page as it opens (mobile-page-in) on
  // the same-colored app bg. Sequential, never overlapping.
  const launchTile = (i: number) => {
    setLaunching(true)
    clearTimeout(pendingOpen.current)
    clearTimeout(closeTimer.current)
    pendingOpen.current = setTimeout(() => navigateTile(i), 300) // after the zoom
    closeTimer.current = setTimeout(onClose, 340)                // then reveal the page
  }

  const pick = (i: number) => {
    if (suppressClick.current) return // this "click" was a mouse-drag release
    if (launching) return // already diving in — ignore repeat taps
    const t = tiles[i]
    // A folder opens INTO the carousel (no dive): its members replace the
    // strip behind a back tile. Neither is a page to launch.
    if (t?.type === 'folder') { setOpenFolderId(t.id); return }
    if (t?.type === 'back') { setOpenFolderId(null); return }
    // Off-center tap: glide the tile into the center (OS-switcher feel), THEN
    // launch once the snap settles — one tap does both, the glide is the lead-in
    // to the dive (not a select-then-tap-again).
    if (i !== focused) {
      centerTile(i, true)
      clearTimeout(pendingOpen.current)
      pendingOpen.current = setTimeout(() => launchTile(i), 240)
      return
    }
    launchTile(i)
  }

  // No JS drag path: the strip is a real overflow-x scroller (native momentum +
  // rubber-band on the compositor) with CSS scroll-snap for paging. Only tile
  // TAPS (pick) and the fog tap (onClose) are handled here.

  return createPortal(
    // ws-clear is LOAD-BEARING: a theme class paints an opaque --bg-primary
    // (index.css "Apply theme variables"), which would sit between the fog
    // and the app — the blur then samples the wrapper's own paint and
    // nothing shows through. ws-clear (unlayered, beats the theme rule)
    // makes the wrapper transparent so the fog frosts the REAL app behind.
    <div
      ref={rootRef}
      className={`${themeClass} ws-clear fixed inset-0 z-[200] flex select-none flex-col items-center justify-center ${launching ? 'mobile-launcher-out pointer-events-none' : ''}`}
    >
      {/* Fog — frosted glass per DESIGN_DOC §10.5 (blur + saturate + themed
          tint live in .mobile-fog, index.css). Tap to dismiss. */}
      <div
        className="mobile-fog mobile-fog-in absolute inset-0"
        onClick={onClose}
        aria-hidden
      />

      {/* Opaque app-colored backdrop for the "open app" zoom — covers the
          frosted old page so the icon zooms out on a solid background, and the
          destination page stays hidden until the launcher unmounts. */}
      {launching && <div className="mobile-launcher-bg themed-bg-primary absolute inset-0" aria-hidden />}

      {/* Theme toggle — the editor's Preview/Source segmented pill (.mode-pill
          via ModeToggle), light → dim → dark. Mirrors the account pill's
          placement: top-10 here vs its bottom-10. Hidden while diving in AND
          while the account sheet is open (it would overlap the sheet header). */}
      {!launching && !accountsOpen && (
        <div className="mobile-toggle-in absolute left-1/2 top-10 z-20 -translate-x-1/2">
          {/* Inline pill (not ModeToggle) so the chip's `left` changes in a
              single render — its CSS transition then fires. ModeToggle sets it
              from a useLayoutEffect measurement, which skips the transition. */}
          <div className="mode-pill" style={{ gap: 0, transform: 'scale(0.9)' }}>
            {/* `left` is NOT set here — it's written imperatively post-paint
                (see themeSliderRef) so the CSS transition actually runs. The
                chip stays the DIM accent in every theme (no blue in light/dark). */}
            <span
              ref={themeSliderRef}
              className="mode-pill-slider"
              data-ready
              style={{ width: THEME_SEG, background: LAUNCHER_ACCENT, boxShadow: `0 1px 6px ${LAUNCHER_ACCENT}66` }}
            />
            {THEME_OPTS.map((o) => (
              <button
                key={o.value}
                onClick={() => pickTheme(o.value)}
                title={o.title}
                aria-label={o.title}
                className={`mode-pill-btn justify-center ${o.value === pillTheme ? 'active' : ''}`}
                style={{ width: THEME_SEG, height: THEME_SEG, padding: 0 }}
              >
                {o.icon}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Center-mode carousel. The scroller fills the WHOLE overlay (h-full,
          items-center) so a swipe ANYWHERE on the screen drives the carousel —
          not just on the tile row. Side padding lets first/last tiles reach the
          center; snap keeps one tile centered at rest. Empty-area taps (target
          IS the strip, not a tile) close the launcher. On launch it fades out
          (over the backdrop) so only the solid bg is left before the page. */}
      <div className={`absolute inset-0 ${launching ? 'mobile-launcher-content-out' : 'mobile-switcher-in'}`}>
        <div
          ref={stripRef}
          onScroll={onScroll}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
          // Empty-area tap: in the focused tile's CENTRAL band (its icon down
          // through its dots), enter that page — tapping just under the centered
          // icon / near the dots reads as "open this", not "dismiss". A tap
          // clearly outside the band (upper area, far bottom) still dismisses.
          onClick={(e) => {
            if (e.target !== e.currentTarget || suppressClick.current) return
            const cy = window.innerHeight / 2
            if (e.clientY > cy - 80 && e.clientY < cy + 170) pick(focused)
            else onClose()
          }}
          className="mobile-switcher-strip flex h-full cursor-grab snap-x snap-mandatory items-center gap-8 overflow-x-auto overflow-y-hidden overscroll-x-contain px-[calc(50vw-44px)] [touch-action:pan-x] [scrollbar-width:none] [&.dragging]:cursor-grabbing [&::-webkit-scrollbar]:hidden"
        >
          {tiles.map((tile, i) => {
            // "Current" = the open view: on an /app route the matching app
            // tile, otherwise the active top-level tab (see isCurrentTile).
            // Mini-apps render the real squircle icon (spec or name-derived
            // default), standalone HTML the Globe glyph — same split as the
            // desktop dock (TaskBar.tsx:475-477).
            const isCurrent = isCurrentTile(tile)
            // Glyphs are rendered at their FULL (centered) size — 1.6× the rest
            // display size — and scaled DOWN by the cover-flow transform, so the
            // centered icon paints at native resolution (no upscale blur).
            let glyph: ReactNode
            if (tile.type === 'folder') {
              glyph = <FolderGlyph members={tile.members} fallbackName="App" size={64} className="pointer-events-none" />
            } else if (tile.type === 'back') {
              glyph = <ArrowLeft size={51} className="pointer-events-none" />
            } else if (tile.type === 'app') {
              glyph = tile.kind === 'miniapp'
                ? <MiniappIcon spec={tile.icon ?? null} name={tile.label} size={64} className="pointer-events-none" />
                : <Globe size={51} className="pointer-events-none" />
            } else {
              const Icon = tile.icon
              glyph = <Icon size={51} />
            }
            return (
              <button
                key={tile.key}
                onClick={() => pick(i)}
                // Swipe-up-to-close (running app tiles only — tilePointerDown
                // bails otherwise, so tab tiles never see the gesture).
                onPointerDown={(e) => tilePointerDown(e, tile)}
                onPointerMove={tilePointerMove}
                onPointerUp={(e) => tilePointerEnd(e, tile, false)}
                onPointerCancel={(e) => tilePointerEnd(e, tile, true)}
                aria-label={tile.label}
                // "Drive" / "Workspace" are not unique accessible names on this
                // screen (the header cutout + drive-section menu use them too), so
                // the launcher tiles carry a testid — the sanctioned escape hatch
                // for an ambiguous role+name (e2e/CLAUDE.md). App tiles key on ref.
                data-testid={`launcher-tile-${tile.type === 'app' ? tile.ref : tile.type === 'folder' ? `folder-${tile.id}` : tile.key}`}
                aria-current={isCurrent ? 'page' : undefined}
                // The button itself never scales — only the icon span does (via
                // `.is-focused`), so the label below keeps a constant size AND a
                // constant position. `items-start` + a fixed-height label box
                // (below) anchor every icon to the SAME line, regardless of how
                // long the app name is or whether it wraps to two lines.
                className="relative flex w-[88px] shrink-0 snap-center flex-col items-center gap-2.5"
              >
                {/* Accent marks the CURRENT view and STAYS there (static
                    `is-current` on the active tab) — it does NOT follow the
                    carousel scroll. The container (this well box) NEVER scales,
                    so the selected-tile highlight keeps a fixed size; only the
                    inner .mobile-switcher-glyph scales, written inline per frame
                    by applyScrollEffects. Styling follows the DESKTOP RAIL: idle
                    = bare muted icon, current = the carved .ws-rail-active well +
                    purple bar along the BOTTOM edge. */}
                <span className={`mobile-switcher-tile flex h-[76px] w-[76px] items-center justify-center rounded-2xl ${isCurrent ? 'is-current' : ''}`}>
                  <span className="mobile-switcher-glyph flex items-center justify-center">{glyph}</span>
                  {/* Running indicator — desktop dock convention (TaskBar):
                      active → the is-current well, running-but-inactive → a
                      small dot, pinned-idle → nothing. Absolute (the well is
                      position:relative), so no layout shift; sits in the gap
                      between well and label. */}
                  {!isCurrent && ((tile.type === 'app' && isRunning(tile.kind, tile.ref)) || (tile.type === 'folder' && tile.running)) && (
                    <span
                      aria-hidden
                      className="absolute -bottom-[7px] left-1/2 h-1 w-1 -translate-x-1/2 rounded-full"
                      style={{ background: 'var(--switcher-icon)' }}
                    />
                  )}
                </span>
                {/* Fixed-height label box (2 lines max, clamped) — so a long or
                    wrapping name reserves the SAME vertical space as a short
                    one and can't push the icon off its line. */}
                <span className="mobile-switcher-label line-clamp-2 h-8 w-full text-center text-xs leading-4 themed-text">{tile.label}</span>
                {/* Pin toggle — app tiles only. Pinned reuses the SAME carved
                    well (`is-current`) the selected Keybook/Workspace tile
                    uses; unpinned is a bare muted glyph. Absolutely positioned
                    below the label so tab/app tiles keep identical layout (the
                    carousel geometry cache never shifts). A span, not a nested
                    <button> (invalid HTML) — stopPropagation keeps a pin tap
                    from launching the tile. */}
                {tile.type === 'app' && tile.folderId && (
                  <span
                    role="button"
                    aria-label="Remove from folder"
                    data-testid={`launcher-unfolder-${tile.ref}`}
                    onClick={(e) => { e.stopPropagation(); removeFromFolder(tile) }}
                    className="absolute left-1/2 top-full -mt-2.5 -translate-x-1/2"
                  >
                    <span className="mobile-switcher-tile flex h-6 w-6 items-center justify-center rounded-md">
                      <FolderOutput size={13} />
                    </span>
                  </span>
                )}
                {tile.type === 'app' && !tile.folderId && (
                  <span
                    role="button"
                    aria-label={isPinned(tile.kind, tile.ref) ? 'Unpin' : 'Pin'}
                    aria-pressed={isPinned(tile.kind, tile.ref)}
                    data-testid={`launcher-pin-${tile.ref}`}
                    onClick={(e) => { e.stopPropagation(); togglePin(tile) }}
                    // Positioning lives on THIS bare wrapper — .mobile-switcher-tile
                    // (inner) sets `position: relative`, which beat Tailwind's
                    // `absolute` when both sat on one element and pushed the pin
                    // into the flex flow (app tiles grew taller than tab tiles).
                    // -mt pulls it into the label box's reserved-but-usually-empty
                    // second line, so it hugs the app name instead of floating low.
                    className="absolute left-1/2 top-full -mt-2.5 -translate-x-1/2"
                  >
                    <span className={`mobile-switcher-tile flex h-6 w-6 items-center justify-center rounded-md ${isPinned(tile.kind, tile.ref) ? 'is-current' : ''}`}>
                      <Pin size={13} fill={isPinned(tile.kind, tile.ref) ? 'currentColor' : 'none'} />
                    </span>
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* The app action sheet (long-press on an app tile). */}
      <MobileSheet open={!!actionTile} onClose={() => setActionTile(null)}>
        {actionTile && (() => {
          const tile = actionTile
          const pinned = isPinned(tile.kind, tile.ref)
          const running = isRunning(tile.kind, tile.ref)
          const currentFolder = tile.folderId ? folders.find(f => f.id === tile.folderId) : null
          const targets = folders.filter(f => f.id !== tile.folderId)
          const done = () => setActionTile(null)
          const row = 'flex items-center gap-3 w-full px-4 py-3 text-left text-[15px] themed-text rounded-xl active:themed-bg-tertiary'
          return (
            <div className="flex flex-col gap-1 px-2 pb-4" data-testid="launcher-app-sheet">
              <div className="flex items-center gap-3 px-4 py-3">
                {tile.kind === 'miniapp'
                  ? <MiniappIcon spec={tile.icon ?? null} name={tile.label} size={36} refId={tile.ref} className="pointer-events-none" />
                  : <Globe size={28} className="themed-icon" />}
                <div className="min-w-0">
                  <div className="truncate font-semibold themed-text">{tile.label}</div>
                  {currentFolder && <div className="truncate text-xs themed-text-muted">{currentFolder.title || 'Folder'}</div>}
                </div>
              </div>
              <button className={row} onClick={() => { togglePin(tile); done() }}>
                {pinned ? <PinOff size={18} /> : <Pin size={18} />}
                {pinned ? 'Unpin' : 'Pin'}
              </button>
              {tile.folderId && (
                <button className={row} onClick={() => { removeFromFolder(tile); done() }}>
                  <FolderOutput size={18} /> Remove from folder
                </button>
              )}
              {pinned && targets.length > 0 && (
                <div className="flex flex-col gap-0.5">
                  <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide themed-text-muted">Move to folder</div>
                  {targets.map(f => (
                    <button key={f.id} className={row} onClick={() => { movePinToFolder(tile.kind, tile.ref, f.id); void setShellPinFolder(tile.kind, tile.ref, f.id).catch(() => {}); done() }}>
                      <FolderInput size={18} /> {f.title || 'Folder'}
                    </button>
                  ))}
                </div>
              )}
              {running && (
                <button className={`${row} text-red-400`} onClick={() => { closeApp(tile.kind, tile.ref); void closeShellInstance(tile.kind, tile.ref).catch(() => {}); if (currentAppRef === tile.ref) navigate(homePath); done() }}>
                  <X size={18} /> Close app
                </button>
              )}
            </div>
          )
        })()}
      </MobileSheet>

      {/* Position indicator — mini dots only (one per icon). The active dot
            uses the DIM accent (same as the theme pill's chip) in every theme.
            Floated just below the centered tile row (the strip is now full
            screen), pointer-events-none so it never eats a swipe/tap. */}
        <div className="pointer-events-none absolute left-1/2 top-[calc(50%+98px)] flex -translate-x-1/2 items-center justify-center gap-1.5">
          {tiles.map((t, i) => (
            <span
              key={t.key}
              style={i === focused ? { background: LAUNCHER_ACCENT } : undefined}
              className={`rounded-full transition-all duration-150 ${
                i === focused ? 'h-1.5 w-4' : 'mobile-dot h-1.5 w-1.5'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Switch-account pill — shows the CURRENT identity; tapping toggles
          the account sheet. Stays visible while the sheet is open UNLESS the
          list grows tall enough to crowd it (pillFits, measured). */}
      {pillDisplay && !launching && (!accountsOpen || pillFits) && (
        <button
          onClick={() => setAccountsOpen((o) => !o)}
          aria-label="Switch account"
          aria-expanded={accountsOpen}
          className="mobile-switcher-in absolute bottom-10 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2.5 rounded-full border themed-border themed-bg-secondary py-1.5 pl-2 pr-3.5 shadow-lg active:themed-bg-tertiary"
        >
          <span className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-semibold text-white ${avatarBg(pillDisplay.seed)}`}>
            {pillDisplay.initials}
          </span>
          <span className="max-w-[10rem] truncate text-sm themed-text">{pillDisplay.label}</span>
        </button>
      )}

      {/* The account sheet — a FULL-SCREEN takeover at every size (one
          consistent look, no floating/full-screen mode-switch). The list is
          `safe center`ed: centered while it fits, top-aligned + scrollable
          once it overflows a short screen (so every row stays reachable). Its
          bottom padding band (index.css) is the pill's zone when the pill
          shows and a tap-to-dismiss strip below Log out when it doesn't —
          tapping any empty area (or Esc) closes. */}
      {accountsOpen && (
        // Rendered inline (not portal): the launcher already IS a portal with
        // its own fog stack, and its bottom pill sits above this at z-20.
        // The cards/fog/animation come from the shared sheet — the share view
        // mounts the very same component.
        <AccountSheet
          rows={identityRows}
          onPick={pickIdentity}
          onClose={() => setAccountsOpen(false)}
          sheetRef={sheetRef}
        >
          {/* Account actions live in the SAME scrollable section as the
              identities — still CARDS, but without the glass tile surface the
              identity rows use. Metrics match so the columns align. */}
          {/* §8.7 — hidden only on a RESOLVED refusal. `undefined` is the
              in-flight window and still renders the card, so a slow
              create-limit response cannot read as "you may not". */}
          {canCreateOrg !== false && (
            <AccountActionCard
              icon={<Plus size={16} />}
              label="New organization"
              onClick={() => setShowOrgCreate(true)}
            />
          )}
          <LogOutCard onClick={() => { void signOut(); onClose() }} />
        </AccountSheet>
      )}

      {showOrgCreate && (
        <OrgCreateDialog
          userEmail={user?.email}
          onCreated={(newTenantId, _name, ownerEmail) => {
            setShowOrgCreate(false)
            void queryClient.invalidateQueries({ queryKey: ['mobile-my-tenants'] })
            switchTenant(newTenantId, ownerEmail)
            onClose()
          }}
          onClose={() => setShowOrgCreate(false)}
        />
      )}
    </div>,
    document.body,
  )
}
